import { useEffect } from 'react';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { NmdpSite } from '.generated/templates/Project.NmdpSite.model';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import fnNoOp from 'lib/util/no-op';

// Extend the Window interface to include the doublethedonation property
declare global {
  interface Window {
    doublethedonation?: {
      plugin?: {
        load_plugin: () => void;
      };
    };
    DDCONF?: {
      API_KEY?: string;
    };
  }
}

/**
 * DoubleTheDonation component displays a Double the Donation widget.
 *
 * @param {DoubleTheDonationProps} props - The properties for the component.
 * @returns {JSX.Element} - The rendered component.
 */

export type DoubleTheDonationProps = Sitecore.Override.ComponentBase &
  NmdpSite.DoubleTheDonation.Fields.DoubleTheDonation;

const DoubleTheDonation = ({ fields }: DoubleTheDonationProps): JSX.Element => {
  const apiKey = fields?.apiKey?.value;

  useEffect(() => {
    if (!apiKey) {
      return fnNoOp;
    }
    // Define the DDCONF variable
    window.DDCONF = { API_KEY: apiKey };

    // Boolean flag to track if the script has already loaded and keep DTD quiet
    const isDoubleTheDonationLoaded = !!window.doublethedonation;

    if (isDoubleTheDonationLoaded) {
      // If the script is already loaded, manually initialize the plugin if necessary
      if (window.doublethedonation?.plugin?.load_plugin) {
        window.doublethedonation.plugin.load_plugin();
      }
      return undefined;
    }

    // Dynamically load the Double the Donation script
    const dtdScript = document.createElement('script');
    dtdScript.src = 'https://doublethedonation.com/api/js/ddplugin.js';
    dtdScript.async = true;

    dtdScript.onload = () => {
      if (window.doublethedonation) {
        if (window.doublethedonation.plugin?.load_plugin) {
          window.doublethedonation.plugin.load_plugin();
        }
      }
    };

    document.body.appendChild(dtdScript);

    // Cleanup script and stylesheet on unmount
    return () => {
      // Remove the script dynamically by querying the DOM
      const scriptInDOM = document.querySelector(
        'script[src="https://doublethedonation.com/api/js/ddplugin.js"]'
      );
      if (scriptInDOM) {
        scriptInDOM.remove();
      }

      // Remove the stylesheet dynamically by querying the DOM
      const dtdStylesheet = document.querySelector(
        'link[href="https://doublethedonation.com/api/css/ddplugin.css"]'
      );
      if (dtdStylesheet) {
        dtdStylesheet.remove();
      }
    };
  }, [apiKey]); // Re-run if the API key changes

  if (!fields) {
    return <MissingDataSource />;
  }

  return (
    <div data-component="authorable/NmdpSite/DoubleTheDonation">
      <div id="dd-container">
        <a href="https://doublethedonation.com/matching-grant-resources/matching-gift-basics/">
          Matching Gift
        </a>{' '}
        and{' '}
        <a href="https://doublethedonation.com/matching-grant-resources/volunteer-grant-basics/">
          Volunteer Grant
        </a>{' '}
        information provided by{' '}
        <a title="Double the Donation" href="https://doublethedonation.com">
          <ImageWrapper
            field={{
              value: {
                src: 'https://doublethedonation.com/api/img/powered-by.png',
                alt: 'Powered by Double the Donation',
              },
            }}
          />
        </a>
      </div>
    </div>
  );
};

export default DoubleTheDonation;
